import {action, makeAutoObservable, observable, runInAction, toJS} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {type IJSONProvider} from "data/providers/json/json.provider";
import {IncreaseTarget} from "data/enums";
import {getSquadPercent} from "data/utils/predictions";
import {orderBy} from "lodash";

export interface IStanding {
	squadId: number;
	wins: number;
	ties: number;
	losses: number;
	played: number;
	percentage: number;
	conferenceRank: number;
	roundId: number;
	pointsFor: number;
	pointsAgainst: number;
	conferenceWins: number;
	conferenceTies: number;
	conferenceLosses: number;
	divisionWins: number;
	divisionTies: number;
	divisionLosses: number;
	divisionRank: number;
}

export interface IUserStanding extends IStanding {
	commonWinPercent: number;
	divisionWinPercent: number;
	conferenceWinPercent: number;
	divisionId?: number;
	conferenceId?: number;
	position?: number;
	newPosition?: number;
}

export interface IStandingsStore {
	get standings(): IUserStanding[];
	get userStandings(): IUserStanding[];

	standingsBySquadId(squadId: number): IUserStanding | undefined;
	lastCompleteStandingsBySquadId(squadId: number, roundId: number): IUserStanding | undefined;

	fetchStandings(): Promise<void>;
	increaseWinsOrLosses: (squadId: number, roundId: number, target: IncreaseTarget) => void;
	increaseDivisionWinsOrLosses: (
		squadId: number,
		roundId: number,
		target: IncreaseTarget
	) => void;
	increaseConfWinsOrLosses: (squadId: number, roundId: number, target: IncreaseTarget) => void;
	updatePercents: (squadId: number, roundId: number) => void;
	updateDivisionRanks: (squadsId: number[], roundId: number) => void;
	clearUserList(): void;
}

@injectable()
export class StandingsStore implements IStandingsStore {
	@observable private _list: IUserStanding[] = [];
	@observable private _userList: IUserStanding[] = [];

	get standings() {
		return this._list;
	}

	get userStandings() {
		return this._userList;
	}

	constructor(@inject(Bindings.JSONProvider) private _jSONProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	standingsBySquadId(squadId: number) {
		return this._list.find((el) => el.squadId === squadId);
	}

	lastCompleteStandingsBySquadId(squadId: number, roundId: number) {
		return this.standings.find((it) => it.roundId === roundId && it.squadId === squadId);
	}

	@action
	async fetchStandings() {
		const {data} = await this._jSONProvider.standings();

		runInAction(() => {
			const updatedList = data.items.map((el) => {
				return {
					...el,
					commonWinPercent: getSquadPercent(el.wins, el.losses, el.ties),
					divisionWinPercent: getSquadPercent(
						el.divisionWins,
						el.divisionLosses,
						el.divisionTies
					),
					conferenceWinPercent: getSquadPercent(
						el.conferenceWins,
						el.conferenceLosses,
						el.conferenceTies
					),
				};
			});

			this._list = updatedList;
			this._userList = updatedList;
		});
	}

	@action increaseWinsOrLosses = (squadId: number, roundId: number, target: IncreaseTarget) => {
		const squadRow = this._userList.find(
			(it) => it.squadId === squadId && it.roundId === roundId
		);

		if (squadRow) {
			target === IncreaseTarget.Wins ? (squadRow.wins += 1) : (squadRow.losses += 1);
		}
	};
	@action increaseDivisionWinsOrLosses = (
		squadId: number,
		roundId: number,
		target: IncreaseTarget
	) => {
		const squadRow = this._userList.find(
			(it) => it.squadId === squadId && it.roundId === roundId
		);

		if (squadRow) {
			target === IncreaseTarget.Wins
				? (squadRow.divisionWins += 1)
				: (squadRow.divisionLosses += 1);
		}
	};

	@action increaseConfWinsOrLosses = (
		squadId: number,
		roundId: number,
		target: IncreaseTarget
	) => {
		const squadRow = this._userList.find(
			(it) => it.squadId === squadId && it.roundId === roundId
		);

		if (squadRow) {
			target === IncreaseTarget.Wins
				? (squadRow.conferenceWins += 1)
				: (squadRow.conferenceLosses += 1);
		}
	};

	@action updatePercents = (squadId: number, roundId: number) => {
		const squadRow = this._userList.find(
			(it) => it.roundId === roundId && it.squadId === squadId
		);

		if (squadRow) {
			squadRow.commonWinPercent = getSquadPercent(
				squadRow.wins,
				squadRow.losses,
				squadRow.ties
			);
			squadRow.divisionWinPercent = getSquadPercent(
				squadRow.divisionWins,
				squadRow.divisionLosses,
				squadRow.divisionTies
			);
			squadRow.conferenceWinPercent = getSquadPercent(
				squadRow.conferenceWins,
				squadRow.conferenceLosses,
				squadRow.conferenceTies
			);
		}
	};

	updateDivisionRanks = (squadsId: number[], roundId: number) => {
		const divisionList = this._userList.filter(
			(it) => squadsId.includes(it.squadId) && roundId === it.roundId
		);
		const orderedDivisionList = orderBy(
			divisionList,
			["commonWinPercent", "divisionWinPercent"],
			["desc", "desc"]
		);

		orderedDivisionList.forEach((it, index) => {
			it.divisionRank = index + 1;
		});
	};

	@action clearUserList() {
		this._userList = toJS(this._list);
	}
}
